import Big from 'big.js'
export function formatBalance(num: number, decimal = 2): string {
  const n = new Big(num)
  return n.round(decimal, Big.roundDown).toFixed(decimal)
}

export function parseBalance(balanceStr: string, decimal = 2): number {
  return new Big(balanceStr).round(decimal, Big.roundDown).toNumber()
}

export function preciseMinus(num1: number, num2: number, decimal = 2): number {
  const n1 = new Big(num1)
  const n2 = new Big(num2)
  return n1.minus(n2).round(decimal, Big.roundDown).toNumber()
}

export function preciseAdd(num1: number, num2: number, decimal = 2): number {
  const n1 = new Big(num1)
  const n2 = new Big(num2)
  return n1.plus(n2).round(decimal, Big.roundDown).toNumber()
}

export function preciseDivide(num1: number, num2: number, decimal = 2): number {
  const n1 = new Big(num1)
  const n2 = new Big(num2)
  return n1.div(n2).round(decimal, Big.roundDown).toNumber()
}

export function preciseMultiply(num1: number, num2: number, decimal = 2): number {
  const n1 = new Big(num1)
  const n2 = new Big(num2)
  return n1.times(n2).round(decimal, Big.roundDown).toNumber()
}

export function precisePow(base: number, exponent: number, decimal = 2): number {
  const b = new Big(base)
  return b.pow(exponent).round(decimal, Big.roundDown).toNumber()
}

const invalidNumberRegex = /[^0-9,.-]/
function isValidNumberString(str: string): boolean {
  return !invalidNumberRegex.test(str)
}

export const formatToCommaWithLang = (number: number, lang = navigator.language, options: Intl.NumberFormatOptions = toCommaOptions.maximumTwoDecimalPlaces) => {
  if (Number.isNaN(number)) return '0'
  if (typeof (number) !== 'number') return number
  let userLanguage: string
  const preciseNum = new Big(number).round(options.maximumFractionDigits, Big.roundDown).toNumber()

  try {
    userLanguage = Intl.getCanonicalLocales(lang ?? 'en-US')[0]
  }
  catch (_) {
    userLanguage = 'en-US'
  }
  const formatStr = Intl.NumberFormat(userLanguage, options).format(preciseNum)
  if (isValidNumberString(formatStr)) return formatStr
  return Intl.NumberFormat('en-US', options).format(preciseNum)
}

export const toCommaOptions: {
  forceTwoDecimalPlaces: Intl.NumberFormatOptions
  maximumTwoDecimalPlaces: Intl.NumberFormatOptions
} = {
  forceTwoDecimalPlaces: {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  },
  maximumTwoDecimalPlaces: {
    maximumFractionDigits: 2,
  },
}

const floorToTwoDecimals = (number: number) => new Big(number).round(2, Big.roundDown).toNumber()

export const formatKMStake = (num: number) => {
  if (num < 1000) {
    const flooredNum = floorToTwoDecimals(num)
    return formatToCommaWithLang(flooredNum)
  }
  else if (num >= 1000 && num < 1000000) {
    const formattedNum = floorToTwoDecimals(num / 1000)
    return formatToCommaWithLang(formattedNum) + 'K'
  }
  else if (num >= 1000000) {
    const formattedNum = floorToTwoDecimals(num / 1000000)
    return formatToCommaWithLang(formattedNum) + 'M'
  }
  return formatToCommaWithLang(num)
}

export const formatHoldAndWinAmount = (num: number) => {
  if (num < 1000) {
    const flooredNum = floorToTwoDecimals(num)
    return formatToCommaWithLang(flooredNum, navigator.language, toCommaOptions.forceTwoDecimalPlaces)
  }
  else if (num >= 1000 && num < 1000000) {
    const formattedNum = floorToTwoDecimals(num / 1000)
    return formatToCommaWithLang(formattedNum, navigator.language, toCommaOptions.forceTwoDecimalPlaces) + 'K'
  }
  else if (num >= 1000000) {
    const formattedNum = floorToTwoDecimals(num / 1000000)
    return formatToCommaWithLang(formattedNum, navigator.language, toCommaOptions.forceTwoDecimalPlaces) + 'M'
  }
  return formatToCommaWithLang(num, navigator.language, toCommaOptions.forceTwoDecimalPlaces)
}

export const formatWinAmount = (num: number) => {
  if (Number.isNaN(num)) return '0'
  if (typeof (num) !== 'number') return num
  const preciseNum = new Big(num).round(2, Big.roundDown).toNumber()
  const hasValidDecimal = preciseNum % 1 !== 0
  const options = hasValidDecimal
    ? toCommaOptions.maximumTwoDecimalPlaces
    : {
        maximumFractionDigits: 0,
      }

  return formatToCommaWithLang(preciseNum, navigator.language, options)
}
